/**
 * HomePage - the front page
 * -------------------------
 * Read by somebody who has never heard of this and owes it no patience, so it
 * is short. If a sentence would read equally well on a site selling
 * accounting software, it has been cut.
 *
 * WHAT THIS REPLACED, so nobody rebuilds it by accident: a centred headline on
 * a green-to-blue gradient, three identical feature cards (icon, heading, two
 * lines), a 1-2-3 of numbered steps in matching columns, and two matching
 * pricing cards with a giant FREE. Each of those is on every list of "how to
 * spot a generated site", and together they are unmistakable.
 *
 * Four sections now, and the order matters:
 *
 *   1. The slogan, across the full width, with the sentence and the buttons
 *      sharing a rule underneath it.
 *   2. What it costs. One heading, three sentences.
 *   3. THE REAL JOBS, as the marketplace actually draws them. Near the bottom
 *      on purpose: by the time somebody has scrolled this far they are asking
 *      "what would I actually be looking at?", and the honest answer is these,
 *      exactly as they are. It is also the one thing on the page a template
 *      cannot fake.
 *   4. A way in.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { choreApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useI18n } from '../hooks/useI18n';
import ChoreCard from '../components/ChoreCard';

export default function HomePage() {
  const { isLoggedIn, isStudent, subscriptionsRequired } = useAuth();
  const { t } = useI18n();
  const [chores, setChores] = useState([]);
  const [total, setTotal] = useState(0);

  // If the board is empty the whole section is dropped rather than showing a
  // cheerful placeholder. An empty noticeboard dressed up as a full one is
  // the exact opposite of the point.
  useEffect(() => {
    choreApi
      .browse({ limit: 3, sort: 'newest' })
      .then((data) => {
        setChores(data.chores || []);
        setTotal(data.total || 0);
      })
      .catch(() => setChores([]));
  }, []);

  return (
    <div className="landing">
      {/* ---------------- SLOGAN ---------------- */}
      <section className="landing-hero">
        <div className="container">
          {/* Two keys, not one sentence with markup in it: where the stress
              falls in an Italian sentence is not where it falls in English,
              so the <em> half has to be translatable on its own. */}
          <h1>
            {t('home.headlineA')} <em>{t('home.headlineB')}</em>
          </h1>

          <div className="landing-hero-foot">
            <p className="landing-lede">{t('home.lede')}</p>

            <div className="landing-actions">
              {isLoggedIn ? (
                <>
                  <Link to="/marketplace" className="landing-cta">
                    {t('home.ctaBrowse')} <ArrowRight size={17} />
                  </Link>
                  <Link to="/dashboard" className="landing-cta-quiet">
                    {isStudent ? t('nav.myWork') : t('nav.myChores')} <ArrowRight size={15} />
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/signup/user" className="landing-cta">
                    {t('home.ctaPoster')}
                  </Link>
                  <Link to="/signup/student" className="landing-cta-quiet">
                    {t('home.ctaStudent')} <ArrowRight size={15} />
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- COST ---------------- */}
      <section className="landing-section">
        <div className="container landing-cost">
          <h2 style={{ marginBottom: 16 }}>
            {subscriptionsRequired ? t('home.costPaid') : t('home.costFree')}
          </h2>
          <p>{t('home.costBody')}</p>
        </div>
      </section>

      {/* ---------------- REAL JOBS ---------------- */}
      {chores.length > 0 && (
        <section className="landing-section">
          <div className="container">
            <div className="landing-board-head">
              <div>
                <h2>{t('home.boardTitle')}</h2>
                <p>{t('home.boardBody')}</p>
              </div>
              <Link to="/marketplace" className="landing-cta-quiet">
                {t('home.seeAll', { count: total })} <ArrowRight size={15} />
              </Link>
            </div>

            <div className="grid-cards">
              {chores.map((chore) => (
                <ChoreCard key={chore._id} chore={chore} invitesBackup />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------------- CLOSE ---------------- */}
      {!isLoggedIn && (
        <section className="landing-close">
          <div className="container">
            <h2>{t('home.closeTitle')}</h2>
            <div className="landing-actions">
              <Link to="/marketplace" className="landing-cta">
                {t('home.closeBrowse')} <ArrowRight size={17} />
              </Link>
              <Link to="/signup" className="landing-cta-quiet">
                {t('home.closeSignUp')} <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
